import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Check, Sparkles, ArrowRight } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const plans = [
  {
    name: 'Starter',
    description: 'Get started with your AI career coach and personalized learning.',
    price: 'Free',
    period: '',
    features: ['AI coach access', 'Personalized learning path', 'Daily nudges & tips', 'Progress tracking'],
    cta: 'Get started',
    href: '/onboarding',
    highlighted: false,
    iconBg: 'bg-muted text-muted-foreground',
  },
  {
    name: 'Pro',
    description: 'Unlock full learning plans, deeper insights, and priority support.',
    price: 'Coming soon',
    period: '',
    features: ['Everything in Starter', 'Full learning plans & lessons', 'Advanced analytics', 'Priority support'],
    cta: 'Join waitlist',
    href: 'mailto:support@fiveai.co?subject=Pro%20waitlist',
    highlighted: true,
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    name: 'Team',
    description: 'For organizations: group coaching, admin tools, and custom learning paths.',
    price: 'Contact us',
    period: '',
    features: ['Everything in Pro', 'Team dashboard', 'Custom content & paths', 'Dedicated success manager'],
    cta: 'Contact sales',
    href: 'mailto:partners@fiveai.co?subject=Team%20plan',
    highlighted: false,
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
];

export const PricingPage: React.FC = () => {
  useSEO({ title: 'Pricing - Free Starter & Pro Plans', description: 'Five AI pricing: Free Starter plan with AI coaching and daily nudges. Pro plan at $29/month with unlimited coaching, full learning plans, and advanced analytics. No credit card required to start.', path: '/pricing' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Pricing">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-4xl mx-auto">
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <CreditCard className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Plans</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Simple</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">pricing</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                Start free. Upgrade when you need more—learning plans, teams, and enterprise options.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Free to start · No credit card required
              </p>
            </motion.div>
          </div>
        </section>
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan, i) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    hover
                    className={`h-full p-6 sm:p-8 rounded-2xl border-2 flex flex-col ${
                      plan.highlighted
                        ? 'border-primary/40 bg-gradient-to-br from-primary/10 to-violet-500/10 shadow-lg shadow-primary/5 hover:border-primary/50'
                        : 'bg-gradient-to-br from-muted/50 to-muted/30 hover:border-primary/40'
                    } transition-all duration-300`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${plan.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{plan.name}</h2>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{plan.description}</p>
                    <div className="mt-auto">
                      <p className="text-2xl font-bold text-foreground mb-4">
                        {plan.price}
                        {plan.period && <span className="text-sm font-normal text-muted-foreground">/{plan.period}</span>}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {plan.features.map((f, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="w-4 h-4 shrink-0 text-primary" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {plan.href.startsWith('mailto:') ? (
                        <Button asChild variant={plan.highlighted ? 'default' : 'outline'} size="sm" className="w-full rounded-xl">
                          <a href={plan.href}>{plan.cta}</a>
                        </Button>
                      ) : (
                        <Button asChild variant={plan.highlighted ? 'default' : 'outline'} size="sm" className="w-full rounded-xl gap-2">
                          <Link to={plan.href}>
                            {plan.cta}
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
